import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonitoringServer } from "@/types/monitoring";
import { useCreateServer, useUpdateServer } from "@/hooks/useMonitoring";
import {
  createServerSchema,
  updateServerSchema,
  CreateServerFormData,
  UpdateServerFormData,
} from "../utils/serverSchema";

function getSubmitLabel(
  isPending: boolean,
  isEdit: boolean,
  t: (key: string) => string,
): string {
  if (isPending && isEdit) return t("monitoring.form.saving");
  if (isPending) return t("monitoring.form.creating");
  if (isEdit) return t("monitoring.form.save");
  return t("monitoring.form.create");
}

interface ServerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server?: MonitoringServer | null;
}

export const ServerFormDialog: React.FC<ServerFormDialogProps> = ({
  open,
  onOpenChange,
  server,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {server
              ? t("monitoring.servers.editServer")
              : t("monitoring.servers.addServer")}
          </DialogTitle>
        </DialogHeader>
        {open && (
          <ServerForm
            key={server?.id ?? "create"}
            server={server}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Inner form (remounts via key when switching create/edit) ---

interface ServerFormProps {
  server?: MonitoringServer | null;
  onClose: () => void;
}

const ServerForm: React.FC<ServerFormProps> = ({ server, onClose }) => {
  const { t } = useTranslation();
  const isEdit = !!server;
  const { mutate: createServer, isPending: isCreating } = useCreateServer();
  const { mutate: updateServer, isPending: isUpdating } = useUpdateServer();
  const isPending = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateServerFormData | UpdateServerFormData>({
    resolver: zodResolver(isEdit ? updateServerSchema() : createServerSchema()),
    defaultValues: {
      name: server?.name ?? "",
      url: server?.url ?? "",
      token: "",
      metricRateSeconds: server?.metricRateSeconds ?? 10,
      metricHistoryDays: server?.metricHistoryDays ?? 7,
      pushIntervalSeconds: server?.pushIntervalSeconds ?? 10,
    },
  });

  const onSubmit = (data: CreateServerFormData | UpdateServerFormData) => {
    if (isEdit && server) {
      const payload = { ...data };
      if (!payload.token) delete payload.token;
      updateServer(
        { id: server.id, data: payload },
        {
          onSuccess: () => {
            toast.success(t("monitoring.servers.updateSuccess"));
            onClose();
          },
          onError: () => toast.error(t("monitoring.servers.updateError")),
        },
      );
    } else {
      createServer(data as CreateServerFormData, {
        onSuccess: () => {
          toast.success(t("monitoring.servers.createSuccess"));
          onClose();
        },
        onError: () => toast.error(t("monitoring.servers.createError")),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="name">{t("monitoring.form.name")} *</Label>
        <Input
          id="name"
          placeholder={t("monitoring.form.namePlaceholder")}
          {...register("name")}
        />
        {errors.name && (
          <p className="text-xs text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="url">{t("monitoring.form.url")} *</Label>
        <Input
          id="url"
          placeholder={t("monitoring.form.urlPlaceholder")}
          {...register("url")}
        />
        {errors.url && (
          <p className="text-xs text-destructive">{errors.url.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="token">
          {t("monitoring.form.token")} {isEdit ? "" : "*"}
        </Label>
        <Input
          id="token"
          type="password"
          placeholder={
            isEdit
              ? t("monitoring.form.tokenKeepCurrent")
              : t("monitoring.form.tokenPlaceholder")
          }
          {...register("token")}
        />
        {errors.token && (
          <p className="text-xs text-destructive">{errors.token.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="metricRateSeconds" className="text-xs">
            {t("monitoring.form.metricRateSeconds")}
          </Label>
          <Input
            id="metricRateSeconds"
            type="number"
            min={1}
            {...register("metricRateSeconds", { valueAsNumber: true })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="metricHistoryDays" className="text-xs">
            {t("monitoring.form.metricHistoryDays")}
          </Label>
          <Input
            id="metricHistoryDays"
            type="number"
            min={1}
            {...register("metricHistoryDays", { valueAsNumber: true })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pushIntervalSeconds" className="text-xs">
            {t("monitoring.form.pushIntervalSeconds")}
          </Label>
          <Input
            id="pushIntervalSeconds"
            type="number"
            min={1}
            {...register("pushIntervalSeconds", { valueAsNumber: true })}
          />
        </div>
      </div>

      <Button type="submit" variant="new_default" disabled={isPending}>
        {getSubmitLabel(isPending, isEdit, t)}
      </Button>
    </form>
  );
};
